/**
 * Legacy PHP URL Redirect Handler
 * Handles 301 redirects from legacy .php URLs to clean React routes
 * 
 * This component ensures that any old .php URLs from the legacy site
 * are properly redirected to their clean counterparts to preserve SEO
 * authority and prevent 404 errors.
 */

import { useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

/**
 * Comprehensive map of legacy .php URLs to clean routes
 * Based on common patterns from legacy PHP immigration websites
 */
const PHP_REDIRECT_MAP = {
  // Core Pages
  '/index.php': '/',
  '/home.php': '/',
  '/admin.php': '/admin/login',
  '/login.php': '/admin/login',
  
  // Main Navigation
  '/contact.php': '/contact',
  '/contact-us.php': '/contact',
  '/about.php': '/about',
  '/about-us.php': '/about',
  '/services.php': '/why-zafco',
  '/our-services.php': '/why-zafco',
  '/testimonials.php': '/testimonials',
  '/success-stories.php': '/testimonials',
  '/blogs.php': '/blogs',
  '/blog.php': '/blogs',
  '/news.php': '/blogs',
  '/careers.php': '/careers',
  '/jobs.php': '/careers',
  '/faq.php': '/faq-page',
  '/faqs.php': '/faq-page',
  '/privacy.php': '/privacy-policy',
  '/privacy-policy.php': '/privacy-policy',
  '/terms.php': '/terms-and-conditions',
  '/terms-and-conditions.php': '/terms-and-conditions',
  '/disclaimer.php': '/disclaimer',
  '/refund.php': '/refund-policy',
  '/refund-policy.php': '/refund-policy',
  
  // Canada Immigration
  '/canada.php': '/countries/canada',
  '/canada-pr.php': '/countries/canada/canada-pr-visa',
  '/canada-immigration.php': '/cms/canada-immigration',
  '/express-entry.php': '/cms/canada-express-entry',
  '/canada-express-entry.php': '/cms/canada-express-entry',
  '/pnp.php': '/cms/canada-pnp-skilled-workers',
  '/ontario-pnp.php': '/cms/ontario-pnp',
  '/alberta-pnp.php': '/cms/alberta-pnp',
  '/bc-pnp.php': '/cms/british-columbia-pnp-program',
  '/quebec.php': '/cms/quebec-skilled-worker-program',
  '/canada-work-visa.php': '/cms/canada-work-visa',
  '/canada-study-visa.php': '/cms/canada-study-visa',
  '/canada-visit-visa.php': '/cms/canada-visit-visa',
  
  // Australia Immigration
  '/australia.php': '/skilled/australia/australia-pr-visa',
  '/australia-pr.php': '/skilled/australia/australia-pr-visa',
  '/australia-immigration.php': '/cms/australia-immigration',
  '/australia-189.php': '/cms/australia-skilled-independent-visa-189',
  '/australia-190.php': '/cms/australia-skilled-nominated-visa-190',
  '/australia-491.php': '/cms/nominated-or-sponsored-visa-489',
  '/australia-work-visa.php': '/cms/australia-work-visa',
  '/australia-study-visa.php': '/cms/australia-study-visa',
  '/australia-visit-visa.php': '/cms/australia-visit-visa',
  
  // UK Immigration
  '/uk.php': '/skilled/uk/uk-skilled-worker-visa',
  '/uk-immigration.php': '/cms/uk-immigration',
  '/uk-skilled-worker.php': '/skilled/uk/uk-skilled-worker-visa',
  '/uk-work-visa.php': '/skilled/uk/uk-skilled-worker-visa',
  '/uk-study-visa.php': '/cms/uk-study-visa',
  '/uk-visit-visa.php': '/cms/uk-visit-visa',
  
  // USA Immigration  
  '/usa.php': '/business/usa/eb-5-visa',
  '/usa-immigration.php': '/cms/usa-immigration',
  '/eb5.php': '/business/usa/eb-5-visa',
  '/eb-5.php': '/business/usa/eb-5-visa',
  '/green-card.php': '/cms/usa-green-card',
  '/h1b.php': '/cms/h1b-visa-guide',
  '/usa-work-visa.php': '/cms/usa-work-visa',
  '/usa-study-visa.php': '/cms/us-study-visa',
  '/usa-visit-visa.php': '/cms/usa-visit-visa',
  
  // Germany & Europe
  '/germany.php': '/germany-opportunity-card',
  '/germany-opportunity-card.php': '/germany-opportunity-card',
  '/eu-blue-card.php': '/cms/germany-eu-blue-card',
  '/europe.php': '/cms/europe-visa',
  '/schengen.php': '/cms/schengen-visit-visa',
  
  // Gulf & Middle East
  '/uae.php': '/skilled/uae/uae-golden-visa',
  '/dubai.php': '/skilled/uae/uae-golden-visa',
  '/golden-visa.php': '/skilled/uae/uae-golden-visa',
  '/saudi.php': '/saudi-arabia',
  '/saudi-arabia.php': '/saudi-arabia',
  '/qatar.php': '/contact',
  '/kuwait.php': '/contact',
  
  // New Zealand
  '/new-zealand.php': '/skilled/new-zealand/new-zealand-pr-visa',
  '/nz.php': '/skilled/new-zealand/new-zealand-pr-visa',
  
  // Services & Calculators
  '/calculator.php': '/calculators',
  '/points-calculator.php': '/calculators',
  '/crs-calculator.php': '/calculators',
  '/resume.php': '/resume-writing-services',
  '/resume-writing.php': '/resume-writing-services',
  '/assessment.php': '/visa-pre-assesment',
  '/free-assessment.php': '/visa-pre-assesment',
  
  // Offices
  '/delhi-office.php': '/offices/new-delhi-office',
  '/mumbai-office.php': '/offices/mumbai-office',
  '/bangalore-office.php': '/offices/bangalore-office',
  '/hyderabad-office.php': '/offices/hyderabad-office',
  '/offices.php': '/offices/new-delhi-office',
};

/**
 * Get clean URL from PHP URL
 * @param {string} phpPath - The .php URL path
 * @returns {string} - The clean redirect target
 */
export const getCleanURLFromPHP = (phpPath) => {
  // Direct match
  if (PHP_REDIRECT_MAP[phpPath]) {
    return PHP_REDIRECT_MAP[phpPath];
  }
  
  // Try to extract meaningful slug from path
  const filename = phpPath.replace('.php', '').replace(/^\//, '');
  
  // Check if we can map to a CMS page
  if (filename && filename !== 'index') {
    return `/cms/${filename}`;
  }
  
  // Default to homepage
  return '/';
};

/**
 * Hook to handle legacy PHP URL redirects
 * Used in App.js to globally handle .php URLs
 */
export const usePHPRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    
    // Check if this is a .php URL
    if (path.endsWith('.php')) {
      const cleanPath = getCleanURLFromPHP(path);

      // Perform permanent redirect (replace: true simulates 301 behavior in SPA)
      navigate(cleanPath, { replace: true });
    }
  }, [location.pathname, navigate]);
};

/**
 * Standalone redirect component for .php routes
 * Used as a Route element to catch and redirect .php URLs
 */
export const PHPRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const cleanPath = getCleanURLFromPHP(location.pathname);
    navigate(cleanPath, { replace: true });
  }, [location.pathname, navigate]);

  return null;
};

/**
 * Component wrapper that adds PHP redirect handling
 * Can wrap PublicLayout to handle all .php requests globally
 */
export const LegacyPHPRedirectHandler = ({ children }) => {
  usePHPRedirect();
  return children;
};

export default PHPRedirect;
